// @ts-nocheck
// This large legacy component was migrated to TypeScript by rename only (logic
// unchanged). Type-checking is disabled here to avoid risky edits in a 1,300-line
// file; it should be incrementally typed later (prop types, MUI Grid `size`, etc.).
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
  Typography,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { cryopreservationService } from '../services/cryopreservationService';
import dayjs from 'dayjs';
import { formatDisplayDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import { glassDialogPaperProps, tokenFieldSx, readonlyFieldBg } from '../styles/tokens';
import { resolvePositions, formatPositions } from '../utils/positionUtils';
import { useToast } from './ui/ToastProvider';
import BoxGrid, { BoxGridLegend } from './BoxGrid';

const FIELD_WIDTH = 220;

// Default origin options that will always be available
const DEFAULT_ORIGINS = ['Human', 'Mouse', 'Rat', 'Rabbit', 'Other'];

// Helper function for parsing cell numbers
const parseCellNumber = (value) => {
  if (!value) return '';
  const cleanValue = value.replace(/million/g, '').trim();
  const num = parseFloat(cleanValue);
  return isNaN(num) ? '' : num.toString();
};

const FIELD_OPTIONS = {};

const getFieldType = (field) => {
  if (['origin', 'box_number', 'position_in_box'].includes(field)) return 'select';
  if (field === 'number_of_cells_frozen' || field === 'number_of_vials_frozen') return 'number';
  if (field === 'date_of_cryo') return 'date';
  if (field === 'notes' || field === 'revival_history') return 'textarea';
  return 'text';
};

const initialFormData = {
  cell_line_name: '',
  origin: '',
  passage_number: '',
  frozen_by: '',
  number_of_cells_frozen: '',
  number_of_vials_frozen: '',
  storage_location: 'Liquid Nitrogen',
  box_number: '',
  position_in_box: '',
  date_of_cryo: '',
  time_of_freezing: '',
  notes: '',
  reason_for_change: ''
};

const CryopreservationModal = React.memo(({
  open,
  mode,
  onClose,
  onSubmit,
  item = null,
  loading,
  error,
  onEdit,
  onDelete,
  onRevive,
  existingItems = [],
  onOpenEdit = () => {},
}) => {
  const theme = useTheme();
  const toast = useToast();
  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isBatchUpload = mode === 'batch';
  const { user } = useAuth();
  const userRole = user?.role || 'viewer';
  
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [originOptions, setOriginOptions] = useState(DEFAULT_ORIGINS);
  const [loadingOrigins, setLoadingOrigins] = useState(false);
  const [customOrigin, setCustomOrigin] = useState('');
  const [showCustomOriginInput, setShowCustomOriginInput] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [revivalDetails, setRevivalDetails] = useState({
    vials_to_revive: 1,
    positions_to_revive: [],
    revived_by: '',
    notes: ''
  });
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadMessage, setUploadMessage] = useState("");
  const [columnMapping, setColumnMapping] = useState({});
  const [availableColumns, setAvailableColumns] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [delimiter, setDelimiter] = useState(',');
  const [hasHeader, setHasHeader] = useState(true);
  // Occupied slots in the currently-selected box (for the picker grid + the
  // inline duplicate-position warning). Excludes this record's own slots on edit.
  const [boxOccupancy, setBoxOccupancy] = useState([]);
  const [showBoxGrid, setShowBoxGrid] = useState(false);
  
  const defaultBoxNumbers = Array.from({length: 30}, (_, i) => String(i + 1));
  const defaultPositions = Array.from({length: 100}, (_, i) => String(i + 1));

  const suggestions = {
    'cell_line_name': [...new Set(existingItems.map(item => String(item.cell_line_name)).filter(Boolean))],
    'frozen_by': [...new Set(existingItems.map(item => String(item.frozen_by)).filter(Boolean))],
    'box_number': [...new Set([...defaultBoxNumbers, ...existingItems.map(item => String(item.box_number)).filter(Boolean)])].sort((a, b) => (isNaN(a) || isNaN(b)) ? a.localeCompare(b) : Number(a) - Number(b)),
    'position_in_box': [...new Set([...defaultPositions, ...existingItems.map(item => String(item.position_in_box)).filter(Boolean)])].sort((a, b) => (isNaN(a) || isNaN(b)) ? a.localeCompare(b) : Number(a) - Number(b))
  };

  const optionalColumns = [
    'cell_line_name',
    'origin', 
    'passage_number',
    'frozen_by',
    'number_of_cells_frozen',
    'number_of_vials_frozen',
    'box_number',
    'position_in_box',
    'notes',
    'date_of_cryo',
    'time_of_freezing'
  ];

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx', '.xls'],
      'text/csv': ['.csv']
    },
    multiple: true,
    onDrop: (acceptedFiles) => {
      setUploadFiles(acceptedFiles);
      setUploadError("");
      setUploadMessage("");
      setValidationErrors({});
      setColumnMapping({});
      setAvailableColumns([]);
    }
  });

  const handleFilePreview = async (file) => {
    try {
      const response = await cryopreservationService.previewFile(file, delimiter);
      
      if (response.columns) {
        setAvailableColumns(response.columns);
        // Auto-map columns based on name similarity
        const autoMapped = {};
        response.columns.forEach(col => {
          const matchingOptional = optionalColumns.find(opt => 
            col.toLowerCase().includes(opt.toLowerCase()) || 
            opt.toLowerCase().includes(col.toLowerCase())
          );
          if (matchingOptional) {
            autoMapped[col] = matchingOptional;
          }
        });
        setColumnMapping(autoMapped);
      }
    } catch (error) {
      setUploadError("Failed to preview file: " + (error.message || "Unknown error"));
    }
  };

  const validateColumnMapping = () => {
    // No validation required since all fields are optional
    return true;
  };

  const handleBatchUpload = async (event) => {
    if (event) event.preventDefault();
    setUploadError("");
    setUploadMessage("");
    setIsUploading(true);

    if (!uploadFiles.length) {
      setUploadError("Please select one or more Excel/CSV files to upload.");
      setIsUploading(false);
      return;
    }

    try {
      const formData = new FormData();
      uploadFiles.forEach(file => {
        formData.append("files", file);
      });
      formData.append("column_mapping", JSON.stringify(columnMapping));
      formData.append("delimiter", delimiter);
      formData.append("has_header", hasHeader);
      formData.append("data_structure", JSON.stringify({
        required: [],
        optional: optionalColumns
      }));

      const response = await cryopreservationService.batchUploadCryopreservation(formData);
      
      if (response.results) {
        const successCount = response.results.filter(r => r.status === 'success').length;
        const errorCount = response.results.filter(r => r.status === 'error').length;
        
        if (errorCount > 0) {
          const errorMessages = response.results
            .filter(r => r.status === 'error')
            .map(r => `${r.file}: ${r.message}`)
            .join('\n');
          setUploadError(`Some files had errors:\n${errorMessages}`);
        }
        
        if (successCount > 0) {
          setUploadMessage(`Successfully uploaded ${successCount} out of ${uploadFiles.length} files.`);
          setUploadFiles([]);
          if (onSubmit) onSubmit();
        }
      } else {
        setUploadMessage("Files uploaded successfully");
        setUploadFiles([]);
        if (onSubmit) onSubmit();
      }
    } catch (error) {
      setUploadError(error.message || "An error occurred during upload");
    } finally {
      setIsUploading(false);
    }
  };

  // Design-system field styling (CSS variables adapt to light/dark automatically)
  const fieldStyles = {
    width: '100%',
    ...tokenFieldSx,
    '& .MuiInputBase-input': {
      ...tokenFieldSx['& .MuiInputBase-input'],
      fontFamily: 'var(--font-sans)',
      fontSize: '0.9rem',
      lineHeight: 1.4,
    },
  };

  // Helper: derive a display name from the user's email (e.g. "john.doe@..." → "John Doe")
  const getUserDisplayName = () => {
    if (!user?.email) return '';
    const username = user.email.split('@')[0];
    return username
      .split(/[._-]/)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
  };

  useEffect(() => {
    if (item) {
      const formattedItem = {
        ...item,
        date_of_cryo: item.date_of_cryo ? dayjs(item.date_of_cryo).format('YYYY-MM-DD') : '',
        notes: item.notes || '',
        revival_history: item.revival_history || [],
        // Ensure box_number is a string for consistency with text input, or empty string
        box_number: item.box_number ? String(item.box_number) : '',
        position_in_box: item.position_in_box ? String(item.position_in_box) : '',
        time_of_freezing: item.time_of_freezing || '',
        // Ensure number_of_cells_frozen is correctly parsed/formatted
        number_of_cells_frozen: item.number_of_cells_frozen ? String(item.number_of_cells_frozen) : '',
        number_of_vials_frozen: item.number_of_vials_frozen ? String(item.number_of_vials_frozen) : '',
      };
      setFormData(formattedItem);
      
      // Reset revival details when a new item is loaded
      const availableVials = Number(item.number_of_vials_frozen) || 0;
      const defaultVialsToRevive = availableVials > 0 ? 1 : 0;
      
      setRevivalDetails({
        vials_to_revive: defaultVialsToRevive,
        positions_to_revive: [],
        revived_by: getUserDisplayName(), // Auto-populate Revived By with current user
        notes: ''
      });
    } else {
      // New record — pre-fill Frozen By, Date of Cryo, and Time of Freezing
      setFormData({
        ...initialFormData,
        frozen_by: getUserDisplayName(),
        date_of_cryo: dayjs().format('YYYY-MM-DD'),
        time_of_freezing: dayjs().format('HH:mm'),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, open]);

  // Fetch additional origin options when modal opens
  useEffect(() => {
    const loadOrigins = async () => {
      if (open) {
        setLoadingOrigins(true);
        try {
          const options = await cryopreservationService.getFilterOptions();
          // Combine filter origins with defaults and remove duplicates
          const originValues = options.origins.map(o => o.value || o);
          const allOrigins = [...new Set([...DEFAULT_ORIGINS, ...originValues])].sort();
          setOriginOptions(allOrigins);
        } catch (error) {
          console.error('Failed to fetch origins:', error);
          // Keep using default options if API fails
          setOriginOptions(DEFAULT_ORIGINS);
        }
        setLoadingOrigins(false);
      }
    };
    loadOrigins();
  }, [open]);

  // Load occupied slots for the chosen box so we can warn about clashes and let
  // the operator pick from a grid. Skipped in view/batch modes.
  useEffect(() => {
    if (!open || isView || isBatchUpload) return;
    const box = formData.box_number;
    if (!box || !String(box).trim()) {
      setBoxOccupancy([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await cryopreservationService.getBoxOccupancy(box, item?._id);
        if (!cancelled) setBoxOccupancy(data?.occupied || []);
      } catch (err) {
        if (!cancelled) setBoxOccupancy([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isView, isBatchUpload, formData.box_number, item]);

  // Positions that clash with a vial already in this box.
  const occupiedPositions = new Set(boxOccupancy.map((o) => String(o.position)));
  const chosenPositions = resolvePositions(formData.position_in_box).positions;
  const conflictPositions = chosenPositions.filter((p) => occupiedPositions.has(String(p)));

  // Toggle a slot from the picker grid into the position spec.
  const handleToggleSlot = (pos) => {
    const current = resolvePositions(formData.position_in_box).positions.map(String);
    const next = current.includes(String(pos))
      ? current.filter((p) => p !== String(pos))
      : [...current, String(pos)];
    handleInputChange('position_in_box', next.length ? formatPositions(next) : '');
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;
    if (field === 'number_of_cells_frozen') {
      processedValue = parseCellNumber(value);
    }
    setFormData(prev => {
      const next = { ...prev, [field]: processedValue };
      // Vials frozen is derived from the position spec: N positions = N vials.
      if (field === 'position_in_box') {
        const { count, error } = resolvePositions(processedValue);
        if (!error && count > 0) {
          next.number_of_vials_frozen = String(count);
        } else if (!processedValue || !processedValue.trim()) {
          next.number_of_vials_frozen = '';
        }
      }
      return next;
    });
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleRevivalDetailsChange = (field, value) => {
    setRevivalDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOriginChange = (event, newValue) => {
    // If newValue is a string, it's a new custom origin typed by the user (freeSolo)
    // If newValue is an object, it's an existing option selected from the list
    const valueToSet = newValue ? (typeof newValue === 'string' ? newValue : newValue) : '';
    
    // If a new value is entered that is not in the options, add it to originOptions
    if (typeof newValue === 'string' && newValue.trim() !== '' && !originOptions.includes(newValue)) {
      setOriginOptions(prev => [...new Set([...prev, newValue])].sort());
    }
    
    handleInputChange('origin', valueToSet);
    if (fieldErrors.origin) {
      setFieldErrors(prev => ({ ...prev, origin: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (isEdit && !formData.reason_for_change?.trim()) {
      errors.reason_for_change = 'Reason for change is required';
    }
    // Block submission on an invalid position spec (e.g. "15-5" or out of range).
    if (formData.position_in_box && formData.position_in_box.trim()) {
      const { error } = resolvePositions(formData.position_in_box);
      if (error) errors.position_in_box = error;
    }
    // Block submission when chosen slots are already occupied in this box.
    if (!errors.position_in_box && conflictPositions.length > 0) {
      errors.position_in_box =
        `Position${conflictPositions.length > 1 ? 's' : ''} ${conflictPositions.join(', ')} ` +
        `already occupied in box ${formData.box_number}. Choose different slots.`;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        setModalLoading(true);
        setModalError('');
        await onSubmit(formData);
        onClose();
      } catch (err) {
        setModalError(err.message || 'Failed to submit form');
      } finally {
        setModalLoading(false);
      }
    }
  };

  const handleRevive = async () => {
    try {
      setModalLoading(true);
      setModalError("");
      
      if (!item || !item._id) {
        setModalError("Missing item information");
        return;
      }
      
      // Get current vials and convert to number
      const currentVialsCount = Number(item.number_of_vials_frozen) || 0;
      // A record with a position spec revives by *picked positions*; otherwise it
      // falls back to a plain vial count.
      const recordPositions = resolvePositions(item.position_in_box).positions;
      const positionMode = recordPositions.length > 0;
      const pickedPositions = revivalDetails.positions_to_revive || [];
      const vialsToRevive = positionMode
        ? pickedPositions.length
        : (Number(revivalDetails.vials_to_revive) || 0);

      if (!revivalDetails.revived_by?.trim()) {
        setModalError("Please provide the name of the person reviving the vials");
        return;
      }

      if (positionMode && pickedPositions.length === 0) {
        setModalError('Select at least one position to revive');
        return;
      }

      if (!vialsToRevive || vialsToRevive <= 0) {
        setModalError('Number of vials to revive must be greater than 0');
        return;
      }

      if (currentVialsCount <= 0) {
        setModalError("No vials available to revive.");
        return;
      }

      if (vialsToRevive > currentVialsCount) {
        setModalError(`Cannot revive more vials than available. Available: ${currentVialsCount}`);
        return;
      }

      const formattedRevivalDetails = {
        ...(positionMode
          ? { positions_to_revive: pickedPositions }
          : { vials_to_revive: vialsToRevive }),
        revived_by: revivalDetails.revived_by.trim(),
        notes: revivalDetails.notes?.trim() || '',
        date: new Date().toISOString()
      };

      // Calculate expected remaining vials
      const expectedRemainingVials = currentVialsCount - vialsToRevive;

      try {
        // Call the parent component's onRevive function with the item ID and formatted details
        const result = await onRevive(item._id, formattedRevivalDetails);
        
        // Check if the operation was successful (check for success property)
        if (result && result.success) {
          // Show success message with actual remaining vials from backend
          const successMessage = `Successfully revived ${result.vials_revived || vialsToRevive} vial(s). Remaining vials: ${result.vials_remaining || expectedRemainingVials}`;
          toast.success(successMessage);
          onClose();
        } else if (result && result.error) {
          // If there's an error in the result, show it
          setModalError(result.error);
        } else {
          // If we can't determine success or failure, assume success (since the update worked)
          const successMessage = `Successfully revived ${vialsToRevive} vial(s). Remaining vials: ${expectedRemainingVials}`;
          toast.success(successMessage);
          onClose();
        }
      } catch (err) {
        // Check for specific error types
        if (err.message && err.message.includes('not found')) {
          // Record not found error - suggest refreshing
          setModalError('This record could not be found. It may have been deleted. Please refresh the page to see the latest data.');
        } else if (err.message && err.message.includes('vials than')) {
          // Vial count error
          setModalError('Cannot revive more vials than available. The record may have been updated. Please refresh the page.');
        } else {
          // Generic error
          setModalError(err.message || 'Failed to revive cell. Please try again.');
        }
      }
    } catch (err) {
      setModalError(err.message || 'Failed to revive cell. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const renderField = (field, label, required = false) => {
    const type = getFieldType(field);
    const isView = mode === 'view';
    const value = formData[field] || '';
    const error = fieldErrors[field];
    // Notes / revival history span the full grid width; everything else is one cell.
    const fullSpan = field === 'notes' || field === 'revival_history';
    const cellSx = { minWidth: 0, gridColumn: fullSpan ? '1 / -1' : 'auto' };
    const displayValue = isView
      ? (field === 'date_of_cryo' ? formatDisplayDate(value) : (value || '-'))
      : value;

    // Special handling for origin, cell_line_name, box_number, and position_in_box (editable autocomplete)
    // frozen_by is read-only (system-set to current user)
    if ((field === 'origin' || field === 'cell_line_name' || field === 'box_number' || field === 'position_in_box') && !isView) {
      const fieldSuggestions = suggestions[field] || (field === 'origin' ? originOptions : []);
      const handleFieldChange = field === 'origin' ? handleOriginChange : (event, newValue) => {
        handleInputChange(field, newValue || '');
      };

      return (
        <Grid grid={{ xs: 12, sm: field === 'notes' ? 12 : 3, lg: field === 'notes' ? 12 : 3 }} sx={cellSx}>
          <Box sx={{ mb: 2, width: '100%' }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {label} {required && <span style={{ color: 'var(--color-accent)' }}>*</span>}
            </Typography>
            <Autocomplete
              freeSolo
              options={fieldSuggestions}
              value={value}
              onChange={handleFieldChange}
              // Only sync the typed text into form state here. Do NOT route typing
              // through the commit handler — otherwise every partial keystroke
              // ("H", "Hu", "Hua"...) gets registered as a selectable option.
              // New values are added to the option list only on commit (onChange,
              // i.e. selecting an item or pressing Enter) and persist for future
              // use once the record is saved.
              onInputChange={(event, newInputValue) => {
                handleInputChange(field, newInputValue || '');
              }}
              disabled={isView}
              loading={field === 'origin' ? loadingOrigins : false}
              fullWidth
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label=""
                  placeholder={`Select or enter`}
                  error={!!error}
                  helperText={error}
                  sx={{ ...fieldStyles }}
                  InputProps={{
                    ...params.InputProps,
                    ...(field === 'origin' && {
                      endAdornment: (
                        <>
                          {loadingOrigins ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option}
                </li>
              )}
              ListboxProps={{
                sx: {
                  bgcolor: 'var(--color-surface)',
                  boxShadow: 'var(--shadow-e2)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 1
                }
              }}
            />
            {error && (
              <FormHelperText error>
                {error}
              </FormHelperText>
            )}
            {field === 'position_in_box' && (() => {
              // Live feedback: how many slots the spec resolves to (e.g. "5-15").
              const { count, error: posError } = resolvePositions(value);
              if (posError) {
                return <FormHelperText error>{posError}</FormHelperText>;
              }
              if (count > 0) {
                return (
                  <FormHelperText sx={{ color: 'var(--color-fg-muted)' }}>
                    {count} position{count === 1 ? '' : 's'} → {count} vial{count === 1 ? '' : 's'}
                  </FormHelperText>
                );
              }
              return (
                <FormHelperText sx={{ color: 'var(--color-fg-muted)' }}>
                  e.g. 5, 5-15, or 5,6,7
                </FormHelperText>
              );
            })()}
          </Box>
        </Grid>
      );
    }

    // frozen_by is always read-only — auto-set to the signed-in user
    if (field === 'frozen_by' && !isView) {
      return (
        <Grid grid={{ xs: 12, sm: 3, lg: 3 }} sx={cellSx}>
          <Box sx={{ mb: 2, width: '100%' }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {label} {required && <span style={{ color: 'var(--color-accent)' }}>*</span>}
            </Typography>
            <TextField
              value={value}
              sx={{
                ...fieldStyles,
                '& .MuiOutlinedInput-root': {
                  cursor: 'not-allowed',
                  bgcolor: readonlyFieldBg,
                },
              }}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <i className="fas fa-lock" style={{ fontSize: '0.75rem', opacity: 0.5 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Grid>
      );
    }

    if (isView) {
      // Special handling for revival_history field
      if (field === 'revival_history') {
        // Format revival history as a string for display
        let historyString = '-';
        try {
          let historyArray = [];
          if (typeof value === 'string' && value.trim()) {
            historyArray = JSON.parse(value);
          } else if (Array.isArray(value)) {
            historyArray = value;
          }
          if (historyArray && Array.isArray(historyArray) && historyArray.length) {
            historyString = historyArray.map((entry, idx) => {
              // Format date to be more readable (just date part)
              const dateStr = entry.date ? formatDisplayDate(entry.date) : 'Unknown date';
              const vialsRevived = entry.vials_revived || entry.number_of_vials_revived || 0;
              const revivedBy = entry.revived_by || 'Unknown';
              const notes = entry.notes ? ` (${entry.notes})` : '';
              
              return `${dateStr}: ${vialsRevived} vial${vialsRevived !== 1 ? 's' : ''} by ${revivedBy}${notes}`;
            }).join('\n');
          }
        } catch (e) {}
        return (
          <Grid grid={{ xs: 12, sm: 3, lg: 3 }} sx={cellSx}>
            <Box sx={{ mb: 2, width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Revival History
              </Typography>
              <TextField
                value={historyString}
                InputProps={{ readOnly: true }}
                multiline
                minRows={3}
                maxRows={8}
                sx={{
                  ...fieldStyles,
                  width: '100%',
                  minWidth: FIELD_WIDTH,
                  maxWidth: '100%',
                  pointerEvents: 'none',
                  '& .MuiOutlinedInput-root': {
                    bgcolor: readonlyFieldBg,
                  },
                }}
              />
            </Box>
          </Grid>
        );
      }
      
      return (
        <Grid grid={{ xs: 12, sm: field === 'notes' ? 12 : 3, lg: field === 'notes' ? 12 : 3 }} sx={cellSx}>
          <Box sx={{ 
            mb: 2,
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {label} {required && <span style={{ color: 'var(--color-accent)' }}>*</span>}
            </Typography>
            <TextField
              value={displayValue}
              InputProps={{ readOnly: true }}
              multiline={field === 'notes'}
              minRows={1}
              maxRows={field === 'notes' ? undefined : 1}
              sx={{
                ...fieldStyles,
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                pointerEvents: 'none',
                '& .MuiOutlinedInput-root': {
                  bgcolor: readonlyFieldBg,
                },
              }}
            />
          </Box>
        </Grid>
      );
    }

    if (type === 'select') {
      return (
        <Grid grid={{ xs: 3, sm: 3, lg: 3 }} sx={cellSx}>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {label} {required && <span style={{ color: 'var(--color-accent)' }}>*</span>}
            </Typography>
            <TextField
              select
              value={value || ''}
              onChange={e => handleInputChange(field, e.target.value)}
              required={required}
              error={!!error}
              helperText={error}
              sx={{ ...fieldStyles }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: { mt: 1 }
                  }
                },
              }}
            >
              {FIELD_OPTIONS[field].map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </Box>
        </Grid>
      );
    }

    if (field === 'number_of_cells_frozen') {
      return (
        <Grid grid={{ xs: 3, sm: 3, lg: 3 }} sx={cellSx}>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {label} {required && <span style={{ color: 'var(--color-accent)' }}>*</span>}
            </Typography>
            <TextField
              value={value || ''}
              onChange={e => handleInputChange(field, e.target.value)}
              type="text" // Changed to text to allow for "million" suffix
              required={required}
              error={!!error}
              helperText={error}
              disabled={isView}
              sx={{ ...fieldStyles }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {value.includes('million') ? '' : 'million'}
                  </InputAdornment>
                ),
              }}
            />
            {error && (
              <FormHelperText error>
                {error}
              </FormHelperText>
            )}
          </Box>
        </Grid>
      );
    }

    if (field === 'number_of_vials_frozen') {
      // Derived from the position spec (N positions = N vials). Read-only while a
      // valid spec is present; falls back to a manual entry when no positions.
      const posInfo = resolvePositions(formData.position_in_box);
      const derived = !posInfo.error && posInfo.count > 0;
      return (
        <Grid grid={{ xs: 3, sm: 3, lg: 3 }} sx={cellSx}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {label} {required && <span style={{ color: 'var(--color-accent)' }}>*</span>}
            </Typography>
            <TextField
              value={derived ? String(posInfo.count) : (value || '')}
              onChange={e => handleInputChange(field, e.target.value)}
              type="text"
              required={required}
              error={!!error}
              helperText={error || (derived ? 'Auto-set from positions' : undefined)}
              disabled={isView}
              sx={{
                ...fieldStyles,
                ...(derived && {
                  '& .MuiOutlinedInput-root': { bgcolor: readonlyFieldBg, cursor: 'not-allowed' },
                }),
              }}
              InputProps={{
                readOnly: derived,
                endAdornment: derived ? (
                  <InputAdornment position="end">
                    <i className="fas fa-lock" style={{ fontSize: '0.75rem', opacity: 0.5 }} />
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Box>
        </Grid>
      );
    }

    if (field === 'date_of_cryo') {
      return (
        <Grid grid={{ xs: 12, sm: 3, lg: 3 }} sx={cellSx}>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {label} {required && <span style={{ color: 'var(--color-accent)' }}>*</span>}
            </Typography>
            <TextField
              fullWidth
              value={formatDisplayDate(value)}
              required={required}
              error={!!error}
              helperText={error}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                readOnly: true,
                endAdornment: !isView ? (
                  <InputAdornment position="end">
                    <i className="fas fa-lock" style={{ fontSize: '0.75rem', opacity: 0.5 }} />
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{
                ...fieldStyles,
                '& .MuiOutlinedInput-root': {
                  cursor: 'not-allowed',
                  bgcolor: isView ? undefined : readonlyFieldBg,
                },
                pointerEvents: isView ? undefined : 'none',
              }}
            />
            {error && (
              <FormHelperText error>
                {error}
              </FormHelperText>
            )}
          </Box>
        </Grid>
      );
    }

    if (field === 'time_of_freezing' && !isView) {
      return (
        <Grid grid={{ xs: 12, sm: 3, lg: 3 }} sx={cellSx}>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {label} {required && <span style={{ color: 'var(--color-accent)' }}>*</span>}
            </Typography>
            <TextField
              fullWidth
              type="time"
              value={value || ''}
              onChange={e => handleInputChange(field, e.target.value)}
              required={required}
              error={!!error}
              helperText={error}
              disabled={isView}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                readOnly: isView,
                endAdornment: isView ? (
                  <InputAdornment position="end">
                    <i className="fas fa-lock" style={{ fontSize: '0.75rem', opacity: 0.5 }} />
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{
                ...fieldStyles,
                '& .MuiOutlinedInput-root': {
                  cursor: isView ? 'not-allowed' : undefined,
                  bgcolor: isView ? readonlyFieldBg : undefined,
                },
                pointerEvents: isView ? 'none' : undefined,
              }}
            />
            {error && (
              <FormHelperText error>
                {error}
              </FormHelperText>
            )}
          </Box>
        </Grid>
      );
    }

    return (
      <Grid grid={{ xs: field === 'revival_history' || field === 'notes' ? 12 : 3, sm: field === 'revival_history' || field === 'notes' ? 12 : 3, lg: field === 'revival_history' || field === 'notes' ? 12 : 3 }} sx={cellSx}>
        <Box sx={{ 
          mb: 2,
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            {label} {required && <span style={{ color: 'var(--color-accent)' }}>*</span>}
          </Typography>
          <TextField
            value={value || ''}
            onChange={e => handleInputChange(field, e.target.value)}
            type={type === 'textarea' ? 'text' : type}
            required={required}
            error={!!error}
            helperText={error}
            disabled={isView}
            multiline={type === 'textarea'}
            minRows={type === 'textarea' ? 3 : 1}
            maxRows={field === 'revival_history' || field === 'notes' ? undefined : 1}
            sx={{
              ...fieldStyles,
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
            }}
          />
        </Box>
      </Grid>
    );
  };

  // Full-width section divider used to group the form fields.
  const SectionLabel = ({ children }) => (
    <Box sx={{ gridColumn: '1 / -1', mt: 1.5, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Typography
        sx={{
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--color-primary)',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'var(--color-outline)' }} />
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth PaperProps={{
      className: glassDialogPaperProps.className,
      sx: {
        ...glassDialogPaperProps.sx,
        width: isBatchUpload ? 'min(720px, 95vw)' : 'min(1080px, 95vw)',
        maxWidth: '95vw',
        transition: 'background 0.3s, box-shadow 0.3s',
      },
    }}>
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          p: 2,
          fontWeight: 'bold',
          bgcolor: 'transparent'
        }}
      >
        <i className={`fas ${isView ? 'fa-info-circle' : isEdit ? 'fa-edit' : isBatchUpload ? 'fa-upload' : 'fa-plus-circle'}`} style={{ marginRight: 8 }}></i>
        {isView ? 'Cryopreservation Details' : isEdit ? 'Edit Cryopreservation' : isBatchUpload ? 'Batch Upload Cryo Data' : 'Add Cryopreservation'}
      </DialogTitle>
      <DialogContent 
        dividers 
        sx={{ 
          bgcolor: 'transparent',
          overflow: 'auto',
          p: 3
        }}
      >
        {(error || modalError || uploadError) && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
          >
            {error || modalError || uploadError}
          </Alert>
        )}
        
        {uploadMessage && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }}
            onClose={() => setUploadMessage("")}
          >
            {uploadMessage}
          </Alert>
        )}

        {isBatchUpload ? (
          <form onSubmit={handleBatchUpload}>
            <Box 
              {...getRootProps()} 
              sx={{
                border: '2px dashed',
                borderColor: isDragActive ? 'var(--color-primary)' : 'var(--color-outline-strong)',
                p: 6,
                borderRadius: 'var(--radius-md)',
                minHeight: 220,
                textAlign: 'center',
                bgcolor: 'var(--color-surface-2)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'var(--color-primary-container)',
                  borderColor: 'var(--color-primary)'
                }
              }}
            >
              <input {...getInputProps()} />
              <Box>
                <i className="fas fa-file-excel" style={{ fontSize: 32, marginRight: 8 }} />
                <Typography variant="h6">Drag & drop Excel/CSV files here, or click to select files</Typography>
              </Box>
            </Box>

            {uploadFiles.length > 0 && (
              <Box mt={3}>
                <Typography variant="subtitle1" gutterBottom>
                  Selected Files:
                </Typography>
                <Box component="ul" pl={2}>
                  {uploadFiles.map((file, index) => (
                    <Box component="li" key={index} display="flex" alignItems="center" mb={1}>
                      <Box flexGrow={1}>{file.name}</Box>
                      <Button
                        size="small"
                        onClick={() => handleFilePreview(file)}
                        disabled={isUploading}
                      >
                        Preview
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {availableColumns.length > 0 && (
              <Box mt={3}>
                <Grid container spacing={2}>
                  <Grid grid={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>CSV Delimiter</InputLabel>
                      <Select
                        value={delimiter}
                        onChange={(e) => setDelimiter(e.target.value)}
                        label="CSV Delimiter"
                      >
                        <MenuItem value=",">Comma (,)</MenuItem>
                        <MenuItem value=";">Semicolon (;)</MenuItem>
                        <MenuItem value="\t">Tab</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid grid={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>File Has Header Row</InputLabel>
                      <Select
                        value={hasHeader}
                        onChange={(e) => setHasHeader(e.target.value)}
                        label="File Has Header Row"
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                  Column Mapping
                </Typography>
                <Box mt={1}>
                  {availableColumns.map((col) => (
                    <Box key={col} mb={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{col}</InputLabel>
                        <Select
                          value={columnMapping[col] || ''}
                          onChange={(e) => setColumnMapping(prev => ({
                            ...prev,
                            [col]: e.target.value
                          }))}
                          label={col}
                        >
                          <MenuItem value="">Not Mapped</MenuItem>
                          {optionalColumns.map((targetCol) => (
                            <MenuItem key={targetCol} value={targetCol}>
                              {targetCol}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {validationErrors[columnMapping[col]] && (
                        <Typography color="error" variant="caption">
                          {validationErrors[columnMapping[col]]}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </form>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              columnGap: 2.5,
              rowGap: 0.5,
              alignItems: 'start',
            }}
          >
            <SectionLabel>Identity</SectionLabel>
            {renderField('cell_line_name', 'Cell Line Name', true)}
            {renderField('origin', 'Origin', true)}
            {renderField('passage_number', 'Passage Number', true)}
            {/* Frozen By is auto-set to the signed-in user — only shown in view mode */}
            {isView && renderField('frozen_by', 'Frozen By', true)}

            <SectionLabel>Storage</SectionLabel>
            {renderField('number_of_cells_frozen', 'Cells Frozen (In Millions)', true)}
            {renderField('number_of_vials_frozen', 'Vials Frozen/Remaining', true)}
            {renderField('box_number', 'Box Number', true)}
            {renderField('position_in_box', 'Position in Box', true)}

            {/* Live duplicate-slot feedback + optional visual picker */}
            {formData.box_number && String(formData.box_number).trim() && (
              <Box sx={{ gridColumn: '1 / -1', mb: 1 }}>
                {conflictPositions.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 'var(--radius-sm)' }}>
                    Position{conflictPositions.length > 1 ? 's' : ''}{' '}
                    <strong>{conflictPositions.join(', ')}</strong> {conflictPositions.length > 1 ? 'are' : 'is'}{' '}
                    already occupied in box {formData.box_number}. Pick different slots.
                  </Alert>
                )}
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowBoxGrid((v) => !v)}
                  startIcon={<i className={`fas ${showBoxGrid ? 'fa-chevron-up' : 'fa-table-cells'}`} />}
                  sx={{ textTransform: 'none', color: 'var(--color-primary)' }}
                >
                  {showBoxGrid ? 'Hide box map' : `Pick slots from box ${formData.box_number} map`}
                </Button>
                {showBoxGrid && (
                  <Box sx={{ mt: 1.5, p: 2, bgcolor: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-outline)' }}>
                    <BoxGrid
                      occupied={boxOccupancy}
                      selected={chosenPositions}
                      conflicts={conflictPositions}
                      onToggle={handleToggleSlot}
                    />
                    <BoxGridLegend />
                  </Box>
                )}
              </Box>
            )}

            <SectionLabel>Details</SectionLabel>
            {/* Date of Cryo is auto-captured — only shown in view mode */}
            {isView && renderField('date_of_cryo', 'Date of Cryo', true)}
            {isView && renderField('time_of_freezing', 'Time of Freezing', false)}
            {isView && renderField('revival_history', 'Revival History', false)}
            {renderField('notes', 'Notes', false)}
            {isEdit && renderField('reason_for_change', 'Reason for Change', true)}
          </Box>
        )}
        
        {/* Revival section */}
        {item && isView && userRole !== 'viewer' && (
          <Box sx={{
            mt: 3,
            p: 2,
            bgcolor: 'var(--color-surface)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-outline)',
          }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: 'var(--color-fg)',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <i className="fas fa-vial" style={{ color: 'var(--color-sage)', fontSize: '0.9rem' }} />
              Revive Cell
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '260px minmax(0, 1fr) auto' },
                gap: 2.5,
                alignItems: 'start',
              }}
            >
              {/* Vials to Revive — pick specific positions when the record has a
                  position spec, otherwise fall back to a plain count.
                  "Revived By" is auto-set to the signed-in user and is not shown. */}
              {(() => {
                const recordPositions = resolvePositions(item.position_in_box).positions;
                const positionMode = recordPositions.length > 0;

                if (positionMode) {
                  return (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Positions to Revive
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          multiple
                          value={revivalDetails.positions_to_revive}
                          onChange={(e) => {
                            const val = typeof e.target.value === 'string'
                              ? e.target.value.split(',')
                              : e.target.value;
                            handleRevivalDetailsChange('positions_to_revive', val);
                          }}
                          renderValue={(selected) => (selected || []).join(', ')}
                          sx={{ ...fieldStyles }}
                          error={!!modalError && modalError.toLowerCase().includes('position')}
                        >
                          {recordPositions.map((pos) => (
                            <MenuItem key={pos} value={pos}>{pos}</MenuItem>
                          ))}
                        </Select>
                        <Typography variant="caption" sx={{ color: 'var(--color-muted)', mt: 0.5 }}>
                          {(revivalDetails.positions_to_revive || []).length} of {recordPositions.length} selected
                        </Typography>
                      </FormControl>
                    </Box>
                  );
                }

                return (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Vials to Revive
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={revivalDetails.vials_to_revive}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          const availableVials = Number(item.number_of_vials_frozen) || 0;
                          const validValue = Math.min(value, availableVials);
                          handleRevivalDetailsChange('vials_to_revive', validValue);
                        }}
                        sx={{ ...fieldStyles }}
                        error={!!modalError && modalError.includes('vials')}
                        disabled={!Number(item.number_of_vials_frozen) || Number(item.number_of_vials_frozen) <= 0}
                      >
                        {Number(item.number_of_vials_frozen) > 0 ? (
                          Array.from({ length: Math.min(Number(item.number_of_vials_frozen) || 0, 5) }, (_, i) => {
                            const value = i + 1;
                            return (
                              <MenuItem key={value} value={value}>
                                {value} {value === 1 ? 'vial' : 'vials'}
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem disabled value={0}>No vials available</MenuItem>
                        )}
                      </Select>
                      {!!modalError && modalError.includes('vials') && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {modalError}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                );
              })()}
              {/* Notes */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Notes
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Optional note about this revival"
                  value={revivalDetails.notes}
                  onChange={(e) => handleRevivalDetailsChange('notes', e.target.value)}
                  sx={{ ...fieldStyles }}
                />
              </Box>
              {/* Revive Button — hidden label keeps it on the same plane as the inputs */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, visibility: 'hidden' }}>
                  Action
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleRevive}
                  disabled={loading || modalLoading || !revivalDetails.revived_by}
                  sx={{ height: 56, px: 3, whiteSpace: 'nowrap' }}
                  startIcon={loading || modalLoading ? <CircularProgress size={20} color="inherit" /> : <i className="fas fa-vial" />}
                >
                  {loading || modalLoading ? 'Reviving...' : 'Revive Cell'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {isBatchUpload ? (
          <>
            <Button variant="outlined" 
            color="secondary" 
            sx={{
              borderRadius: 2,
              borderWidth: 2,
              minWidth: 180
            }}
            onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{
                borderRadius: 2,
                minWidth: 180
              }}
              onClick={handleBatchUpload}
              disabled={uploadFiles.length === 0 || isUploading}
              startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <i className="fas fa-upload" />}
            >
              {isUploading ? 'Uploading...' : 'Upload Files'}
            </Button>
          </>
        ) : (
          isView ? (
            <>
              {userRole !== 'viewer' && (
                <Button 
                  color="error"
                  variant="outlined"
                  onClick={() => onDelete(item._id)}
                  disabled={loading}
                  startIcon={<i className={userRole === 'user' ? 'fas fa-paper-plane' : 'fas fa-trash'}></i>}
                  sx={{ 
                    borderRadius: 2,
                    width: 180,
                    borderWidth: 2,
                  }}
                >
                  {loading ? (
                    <Box display="flex" alignItems="center" gap={2}><CircularProgress size={18} /> {userRole === 'user' ? 'Requesting...' : 'Deleting...'}</Box>
                  ) : (
                    userRole === 'user' ? 'Request Deletion' : 'Delete Cell Line'
                  )}
                </Button>
              )}

              {userRole !== 'viewer' && (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => onOpenEdit(item)}
                  disabled={loading}
                  startIcon={<i className="fas fa-edit"></i>}
                  sx={{
                    borderRadius: 2,
                    width: 180,
                  }}
                >
                  {loading ? (
                    <Box display="flex" alignItems="center" gap={2}><CircularProgress size={18} /> Loading...</Box>
                  ) : (
                    'Edit Details'
                  )}
                </Button>
              )}
              <Button 
                color="primary" 
                variant="outlined" 
                onClick={onClose} 
                sx={{ 
                  borderRadius: 2,
                  width: 180,
                  borderWidth: 2,
                }}
              >
                Close
              </Button>
            </>
          ) : (
            <>
              <Button 
                onClick={onClose} 
                color="primary" 
                disabled={loading} 
                variant="outlined" 
                sx={{ 
                  borderRadius: 2,
                  width: 180,
                  borderWidth: 2,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                color="primary"
                variant="contained"
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  width: 180,
                }}
              >
                {loading ? (
                  <Box display="flex" alignItems="center" gap={2}>
                    <CircularProgress size={18} color="inherit" />
                    Processing...
                  </Box>
                ) : isEdit ? (
                  'Save Changes'
                ) : (
                  'Add Item'
                )}
              </Button>
            </>
          )
        )}
      </DialogActions>
    </Dialog>
  );
});

export default CryopreservationModal;